import { useId } from 'react';
import { Box, Button, InlineStack, Select, TextLabel, Tooltip } from '@customerio/pluma-components/react';
import * as styles from './sort-control.css';

export type SortOption<T extends string> = { label: string; value: T };

type SortControlProps<T extends string> = {
  options: ReadonlyArray<SortOption<T>>;
  /**
   * The sort in effect. One the options leave out, such as a metric sort from a shared URL, picks
   * none of them rather than claiming a sort the list is not in.
   */
  selected: string;
  isDescending: boolean;
  onChange: (value: T) => void;
  onDirectionChange: (isDescending: boolean) => void;
  /** Whether to show a visible "Sort by" label. Without it the select is still labelled for screen readers. */
  showLabel?: boolean;
};

/** A sort field picker joined to a toggle for the direction. */
export function SortControl<T extends string>({
  options,
  selected,
  isDescending,
  onChange,
  onDirectionChange,
  showLabel = true,
}: SortControlProps<T>) {
  const id = useId();
  const labelId = `${id}-label`;

  return (
    <InlineStack alignBlock="center" gap="100">
      {showLabel ? (
        <TextLabel id={labelId} htmlFor={id}>
          Sort by
        </TextLabel>
      ) : null}
      <InlineStack alignBlock="center" gap="0" role="group">
        <Box className={styles.connectedSelect}>
          <Select
            id={id}
            size="sm"
            options={[...options]}
            value={selected}
            ariaLabelledby={showLabel ? labelId : undefined}
            ariaLabel={showLabel ? undefined : 'Sort by'}
            onChange={(value: string | null) => {
              const option = options.find((candidate) => candidate.value === value);
              if (option != null) {
                onChange(option.value);
              }
            }}
          />
        </Box>
        {/* The tooltip and name say what a click does, not the order in effect. */}
        <Tooltip content={isDescending ? 'Sort ascending' : 'Sort descending'} isCustom>
          {(context) => (
            <Button
              variant="secondary"
              size="sm"
              isIconOnly
              icon={isDescending ? 'sort-descending' : 'sort-ascending'}
              aria-label={isDescending ? 'Sort ascending' : 'Sort descending'}
              onClick={() => onDirectionChange(!isDescending)}
              className={styles.connectedButton}
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
            />
          )}
        </Tooltip>
      </InlineStack>
    </InlineStack>
  );
}
